'use client'

import { FC, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { IApiError } from '@/common/interfaces'
import { Button, Input } from '@/components/commons'
import { EyeIcon, EyeNoIcon } from '@/components/icons'
import { useAuthProvider } from '@/contexts/auth/AuthProvider'
import {
  IUpdatePasswordMultiTenantRequest,
  useUpdatePasswordMultiTenantService,
} from '@/services/user/updatePasswordMultiTenant/updatePasswordMultiTenantService'
import { showToast } from '@/utils/toastify/toastify'
import { yupResolver } from '@hookform/resolvers/yup'

import { Modal } from './components'
import { VariantType } from './components/Modal/types'
import { FormProps } from './types'
import { schema } from './utils/validations'

const Form: FC = () => {
  const { logout } = useAuthProvider()

  const { mutateAsync, isPending } = useUpdatePasswordMultiTenantService()

  const [modalVariant, setModalVariant] = useState<VariantType>('success')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isLogoutLoading, setIsLogoutLoading] = useState(false)
  const [toggleEyeCurrentPass, setToggleEyeCurrentPass] = useState(false)
  const [toggleEyePass, setToggleEyePass] = useState(false)
  const [toggleEyeConfirmPass, setToggleEyeConfirmPass] = useState(false)
  const [customMessage, setCustomMessage] = useState<Array<IApiError> | string>(
    [] as Array<IApiError>,
  )

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormProps>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  async function handleChangePassword() {
    const data = getValues()

    const request: IUpdatePasswordMultiTenantRequest = {
      newPassword: data.password,
      oldPassword: data.currentPassword,
    }

    await mutateAsync(request)
      .then((responseData) => {
        const hasError =
          Array.isArray(responseData) &&
          responseData.some((item) => item?.success === false)
        if (hasError) {
          setCustomMessage(responseData)
          setModalVariant('customMessage')
          setIsOpenModal(true)
          return
        }
        showToast('success', 'Senha alterada com sucesso!')
        setIsOpenModal(true)
        setModalVariant('success')
      })
      .catch((error) => {
        if (error?.response?.data?.length) {
          setCustomMessage(error?.response?.data)
          setModalVariant('customMessage')
          setIsOpenModal(true)
          return
        }

        showToast('error', 'Ops. Algo deu errado, tente novamente mais tarde!')
        setIsOpenModal(false)
      })
  }

  const onSubmit: SubmitHandler<FormProps> = async () => {
    handleChangePassword()
  }

  function handleLogout() {
    setIsOpenModal(false)
    setIsLogoutLoading(true)
    logout()
  }

  function handleClickModal() {
    if (modalVariant === 'success') {
      handleLogout()
    }
  }

  return (
    <div
      className={`absolute top-0 left-0 flex items-center justify-center w-screen h-screen bg-black/50 backdrop-blur-sm ${isLogoutLoading ? 'z-10' : 'z-20'}`}
    >
      <div
        className={`flex justify-center p-4 bg-neutral-white rounded-lg ${isLogoutLoading ? 'hidden' : ''}`}
      >
        <form
          className="flex flex-col w-full max-w-[437px] justify-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h6 className="prose-headline-h6 text-primary-900">
            Primeiro acesso
          </h6>
          <p className="prose-body text-primary-900">
            Você deverá cadastrar uma nova senha.
          </p>
          <p className="prose-body text-primary-900">
            Por favor, preencha os campos abaixo para criar a sua nova senha.
          </p>

          <Input
            wFull
            label="Senha atual"
            helperText={errors.currentPassword?.message}
            type={toggleEyeCurrentPass ? 'text' : 'password'}
            classNameIconRight="cursor-pointer"
            iconRight={
              toggleEyeCurrentPass ? (
                <EyeIcon
                  onClick={() => setToggleEyeCurrentPass(!toggleEyeCurrentPass)}
                />
              ) : (
                <EyeNoIcon
                  onClick={() => setToggleEyeCurrentPass(!toggleEyeCurrentPass)}
                />
              )
            }
            {...register('currentPassword')}
          />
          <Input
            wFull
            label="Nova senha"
            helperText={errors.password?.message}
            type={toggleEyePass ? 'text' : 'password'}
            classNameIconRight="cursor-pointer"
            iconRight={
              toggleEyePass ? (
                <EyeIcon onClick={() => setToggleEyePass(!toggleEyePass)} />
              ) : (
                <EyeNoIcon onClick={() => setToggleEyePass(!toggleEyePass)} />
              )
            }
            {...register('password')}
          />
          <Input
            wFull
            label="Confirmar nova senha"
            helperText={errors.confirmPassword?.message}
            type={toggleEyeConfirmPass ? 'text' : 'password'}
            classNameIconRight="cursor-pointer"
            iconRight={
              toggleEyeConfirmPass ? (
                <EyeIcon
                  onClick={() => setToggleEyeConfirmPass(!toggleEyeConfirmPass)}
                />
              ) : (
                <EyeNoIcon
                  onClick={() => setToggleEyeConfirmPass(!toggleEyeConfirmPass)}
                />
              )
            }
            {...register('confirmPassword')}
          />

          <div className="flex w-full justify-between gap-4 mt-4">
            <Button
              type="button"
              variant="secondary"
              text="Sair"
              className={`flex-1 ${isPending ? 'pointer-events-none' : ''}`}
              onClick={handleLogout}
            />
            <Button
              type="submit"
              text="Confirmar"
              className="flex-1"
              isLoading={isPending}
            />
          </div>
        </form>

        <Modal
          variant={modalVariant}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          onClick={handleClickModal}
          customMessage={customMessage}
        />
      </div>
    </div>
  )
}

export default Form
